/**
 * Faction profile & compare factions (website roadmap S14 — FF Scouter's Faction Profile / Compare Factions, Torn Intel's
 * comparison and battle-stat distribution). One copy for the server (apps/api/src/faction-profile.ts, which reads Torn)
 * and the website (apps/web/src/app/pages/factions.ts, which draws it). Pure, browser-safe.
 *
 * - Torn's PUBLIC faction data (v2 `/faction/{id}/basic`, `/members`, `/rankedwars`, `/chain`), read by the server on
 *   demand with lent keys or its own key, kept an hour in memory — nothing stored, nothing sent on.
 * - Estimates come only from Omni-Torn's player database (the same best estimate the war room and Spies & estimates show).
 *   The distribution (average, median, buckets) is worked out here from those.
 */

import type { Activity, RoomMember } from './war-room';
import { readRankedWarList, type RankedWarInfo } from './war';

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : typeof x === 'string' && /^-?\d+(\.\d+)?$/.test(x) ? Number(x) : null);
const str = (x: unknown): string => (typeof x === 'string' ? x.trim() : '');
/** Torn's seconds → ms (0/none → null). */
const ms = (x: unknown): number | null => {
  const n = num(x);
  return n && n > 0 ? n * 1000 : null;
};

// ---------------------------------------------------------------- limits

/** A faction's read is kept this long (roadmap: cached 1 h). */
export const FACTION_PROFILE_KEEP_MS = 3_600_000;
/** Torn calls for one faction: basic, members, ranked wars, chain. */
export const FACTION_PROFILE_CALLS = 4;
/** Torn calls a minute for every asker together (four factions). */
export const FACTION_PROFILE_PER_MINUTE = 12;
/** Torn calls a minute for one asker (two factions: a compare). */
export const FACTION_PROFILE_PER_ASKER = 8;
/** Ranked wars kept per faction (newest first). */
export const FACTION_PROFILE_WARS = 10;

// ---------------------------------------------------------------- what's read

export interface FactionRank {
  /** Torn's rank name ("Gold") and division (1–3, null for unranked). */
  name: string;
  division: number | null;
  level: number | null;
  position: number | null;
  wins: number | null;
}

export interface FactionInfo {
  id: number;
  name: string;
  tag: string;
  respect: number | null;
  /** Days since it was founded. */
  daysOld: number | null;
  capacity: number | null;
  /** Members now (Torn's count). */
  memberCount: number | null;
  leaderId: number | null;
  coLeaderId: number | null;
  bestChain: number | null;
  enlisted: boolean | null;
  rank: FactionRank | null;
}

/** One member as the profile shows them — Torn's public roster (times in ms). */
export interface FactionRosterMember extends RoomMember {
  position: string;
  daysInFaction: number | null;
}

/** The chain going on now (null when none). Times in ms. */
export interface FactionChainNow {
  current: number;
  max: number | null;
  /** Seconds left before it runs out without a hit, when Torn was read. */
  timeout: number | null;
  modifier: number | null;
  start: number | null;
}

/** The player database's best estimate of a member (the same as the war room's). */
export interface FactionMemberEstimate {
  id: number;
  total: number;
  source: string;
  /** ms. */
  at: number;
}

/** GET /api/factions/:id — a faction's public profile, roster, ranked wars and chain (kept an hour), with Omni-Torn's estimates of its members. */
export interface FactionProfileAnswer {
  id: number;
  /** When Torn was read (ms). */
  at: number;
  info: FactionInfo;
  members: FactionRosterMember[];
  /** Newest first, at most FACTION_PROFILE_WARS; null: Torn couldn't be asked for them this time. */
  wars: RankedWarInfo[] | null;
  /** null: no chain going (or Torn couldn't be asked). */
  chain: FactionChainNow | null;
  estimates: FactionMemberEstimate[];
  /** Torn calls this answer cost (0: it was kept). */
  calls: number;
  /** Something that didn't come (wars or chain) — the rest is shown. */
  problem?: string;
}

/** v2 `/faction/{id}/basic` (`{ basic: {...} }`) or v1 `/faction/{id}?selections=basic` → the faction's facts. */
export function readFactionInfo(body: unknown, fallbackId = 0): FactionInfo | null {
  const b = obj(obj(body)?.basic) ?? obj(body);
  if (!b) return null;
  const id = num(b.id ?? b.ID) ?? fallbackId;
  const name = str(b.name);
  if (!id || !name) return null;
  const r = obj(b.rank);
  const membersRaw = b.members;
  const memberCount = num(membersRaw) ?? (Array.isArray(membersRaw) ? membersRaw.length : obj(membersRaw) ? Object.keys(obj(membersRaw)!).length : null);
  return {
    id,
    name,
    tag: str(b.tag),
    respect: num(b.respect),
    daysOld: num(b.days_old ?? b.age),
    capacity: num(b.capacity),
    memberCount,
    leaderId: num(b.leader_id ?? b.leader) || null,
    coLeaderId: num(b.co_leader_id ?? b['co-leader']) || null,
    bestChain: num(b.best_chain),
    enlisted: typeof b.is_enlisted === 'boolean' ? b.is_enlisted : null,
    rank: r ? { name: str(r.name) || 'Unranked', division: num(r.division) || null, level: num(r.level), position: num(r.position), wins: num(r.wins) } : null,
  };
}

const activityOf = (s: string): Activity => (/^online$/i.test(s) ? 'online' : /^idle$/i.test(s) ? 'idle' : 'offline');

/** v2 `/faction/{id}/members` (a list) or v1's `members` map → the roster, highest level first. */
export function readFactionRoster(body: unknown): FactionRosterMember[] {
  const raw = obj(body)?.members;
  const pairs: Array<[number, Obj]> = Array.isArray(raw)
    ? raw.flatMap((m) => (obj(m) && num(obj(m)!.id) ? [[num(obj(m)!.id)!, obj(m)!] as [number, Obj]] : []))
    : Object.entries(obj(raw) ?? {}).flatMap(([k, v]) => (obj(v) && num(k) ? [[num(k)!, obj(v)!] as [number, Obj]] : []));
  return pairs
    .filter(([id]) => Number.isInteger(id) && id > 0)
    .map(([id, m]): FactionRosterMember => {
      const st = obj(m.status);
      const la = obj(m.last_action);
      return {
        id,
        name: str(m.name) || `#${id}`,
        level: num(m.level) ?? 0,
        position: str(m.position),
        daysInFaction: num(m.days_in_faction),
        state: str(st?.state) || 'Okay',
        description: str(st?.description),
        until: ms(st?.until),
        lastAction: ms(la?.timestamp),
        activity: activityOf(str(la?.status)),
      };
    })
    .sort((a, b) => b.level - a.level || a.id - b.id);
}

/** v2 `/faction/{id}/chain` (`{ chain: {...} }`) → the chain going on, or null when there's none. */
export function readFactionChain(body: unknown): FactionChainNow | null {
  const c = obj(obj(body)?.chain);
  const current = num(c?.current);
  if (!c || !current) return null;
  return { current, max: num(c.max), timeout: num(c.timeout), modifier: num(c.modifier), start: ms(c.start) };
}

/** v2 `/faction/{id}/rankedwars` → newest first, at most FACTION_PROFILE_WARS. */
export const readFactionWars = (body: unknown): RankedWarInfo[] => readRankedWarList(body).slice(0, FACTION_PROFILE_WARS);

// ---------------------------------------------------------------- the estimate distribution

/** Buckets of estimated battle stats totals, from the bottom. */
export const ESTIMATE_BUCKETS: ReadonlyArray<{ label: string; from: number; to: number }> = [
  { label: 'under 1m', from: 0, to: 1e6 },
  { label: '1m–10m', from: 1e6, to: 1e7 },
  { label: '10m–100m', from: 1e7, to: 1e8 },
  { label: '100m–500m', from: 1e8, to: 5e8 },
  { label: '500m–1b', from: 5e8, to: 1e9 },
  { label: '1b–2.5b', from: 1e9, to: 2.5e9 },
  { label: '2.5b–5b', from: 2.5e9, to: 5e9 },
  { label: '5b–10b', from: 5e9, to: 1e10 },
  { label: '10b and up', from: 1e10, to: Infinity },
];

export interface EstimateDistribution {
  /** Members on the roster. */
  members: number;
  /** Members with an estimate. */
  known: number;
  average: number | null;
  median: number | null;
  /** The strongest estimate, and the average of the five strongest. */
  top: number | null;
  topFive: number | null;
  /** All estimates added up. */
  sum: number;
  buckets: Array<{ label: string; from: number; to: number; n: number }>;
}

/** Average, median and buckets of the estimates of a roster's members (estimates of anyone not on it are left out). */
export function estimateDistribution(memberIds: readonly number[], estimates: readonly Pick<FactionMemberEstimate, 'id' | 'total'>[]): EstimateDistribution {
  const on = new Set(memberIds);
  const seen = new Set<number>();
  const totals: number[] = [];
  for (const e of estimates) {
    if (!on.has(e.id) || seen.has(e.id) || !(e.total > 0)) continue;
    seen.add(e.id);
    totals.push(e.total);
  }
  totals.sort((a, b) => a - b);
  const n = totals.length;
  const sum = totals.reduce((s, x) => s + x, 0);
  const median = n ? (n % 2 ? totals[(n - 1) / 2]! : (totals[n / 2 - 1]! + totals[n / 2]!) / 2) : null;
  const five = totals.slice(-5);
  return {
    members: on.size,
    known: n,
    average: n ? sum / n : null,
    median,
    top: n ? totals[n - 1]! : null,
    topFive: five.length ? five.reduce((s, x) => s + x, 0) / five.length : null,
    sum,
    buckets: ESTIMATE_BUCKETS.map((b) => ({ ...b, n: totals.filter((t) => t >= b.from && t < b.to).length })),
  };
}

// ---------------------------------------------------------------- compare

export interface FactionCompareRow {
  label: string;
  a: number | null;
  b: number | null;
  /** Which side is ahead ('a' / 'b'), or null (equal, unknown, or "more" isn't better). */
  ahead: 'a' | 'b' | null;
  /** How to show the numbers: a count, a battle stats total (short), or days. */
  kind: 'count' | 'stats' | 'days';
}

/** Two factions side by side: the profile numbers and their estimate distributions. */
export function compareFactions(a: { info: FactionInfo; members: readonly unknown[]; dist: EstimateDistribution; chain: FactionChainNow | null }, b: typeof a): FactionCompareRow[] {
  const row = (label: string, x: number | null, y: number | null, kind: FactionCompareRow['kind'] = 'count', better = true): FactionCompareRow => ({
    label,
    a: x,
    b: y,
    kind,
    ahead: !better || x === null || y === null || x === y ? null : x > y ? 'a' : 'b',
  });
  const online = (m: readonly unknown[]) => m.filter((x) => (x as Partial<RoomMember>).activity === 'online').length;
  return [
    row('Respect', a.info.respect, b.info.respect),
    row('Rank (wins)', a.info.rank?.wins ?? null, b.info.rank?.wins ?? null),
    row('Members', a.members.length || a.info.memberCount, b.members.length || b.info.memberCount),
    row('Online now', online(a.members), online(b.members)),
    row('Best chain', a.info.bestChain, b.info.bestChain),
    row('Chain now', a.chain?.current ?? 0, b.chain?.current ?? 0),
    row('Age', a.info.daysOld, b.info.daysOld, 'days', false),
    row('Members with an estimate', a.dist.known, b.dist.known, 'count', false),
    row('Average estimate', a.dist.average, b.dist.average, 'stats'),
    row('Median estimate', a.dist.median, b.dist.median, 'stats'),
    row('Five strongest (average)', a.dist.topFive, b.dist.topFive, 'stats'),
    row('Strongest', a.dist.top, b.dist.top, 'stats'),
    row('All estimates added up', a.dist.sum || null, b.dist.sum || null, 'stats'),
  ];
}

/** Their ranked wars against each other (from either side's list), newest first. */
export function warsBetween(a: { id: number; wars: readonly RankedWarInfo[] | null }, b: { id: number; wars: readonly RankedWarInfo[] | null }): RankedWarInfo[] {
  const byId = new Map<number, RankedWarInfo>();
  for (const w of [...(a.wars ?? []), ...(b.wars ?? [])]) {
    const ids = w.factions.map((f) => f.id);
    if (ids.includes(a.id) && ids.includes(b.id) && !byId.has(w.id)) byId.set(w.id, w);
  }
  return [...byId.values()].sort((x, y) => y.start - x.start);
}

// ---------------------------------------------------------------- finding a faction

/** What was typed: a faction id ("12345", "[12345]", a Torn faction link) or a name to look for. */
export function parseFactionQuery(raw: string): { id: number } | { name: string } | null {
  const q = raw.trim();
  if (!q) return null;
  const id = /^\[?(\d{1,10})\]?$/.exec(q)?.[1] ?? /factions\.php\?[^#]*\bID=(\d{1,10})/i.exec(q)?.[1] ?? /\[(\d{1,10})\]\s*$/.exec(q)?.[1];
  if (id && Number(id) > 0) return { id: Number(id) };
  return q.length >= 2 && q.length <= 40 ? { name: q.toLowerCase() } : null;
}

/** Factions whose name (or tag) matches, from the lists Omni-Torn has (the Hall of Fame tops, the ranked wars): exact first, then starting so, then containing. */
export function findFactions(known: ReadonlyArray<{ id: number; name: string; tag?: string | null }>, q: string, max = 10): Array<{ id: number; name: string }> {
  const s = q.trim().toLowerCase();
  if (!s) return [];
  const seen = new Map<number, { id: number; name: string; score: number }>();
  for (const f of known) {
    if (!f.id || !f.name) continue;
    const n = f.name.toLowerCase();
    const t = (f.tag ?? '').toLowerCase();
    const score = n === s || t === s ? 0 : n.startsWith(s) ? 1 : n.includes(s) ? 2 : -1;
    if (score < 0) continue;
    const old = seen.get(f.id);
    if (!old || score < old.score) seen.set(f.id, { id: f.id, name: f.name, score });
  }
  return [...seen.values()].sort((a, b) => a.score - b.score || a.name.localeCompare(b.name)).slice(0, max).map(({ id, name }) => ({ id, name }));
}
